import { ITranslatableEntity } from './common';
import { IExpenseType } from './expense';
import { IPreliminaryInvoice } from './invoice';
import { IMatter } from './matter';
import { IMatterParties } from './matterParties';
import { IPrincipalCompany } from './principalCompany';
import { IPrincipalPrivatePerson } from './principalPrivatePerson';
import { IBusinessUnit } from './user';

export interface IParameters {
  parameters: {
    transactionParameters: ITransactionParameters;
    invoiceParameters: IInvoiceParameters;
    defaultParametersResponse: IDefaultParameters;
    featuresInUseParameters: IFeaturesInUseParameters;
    transactionPostingPeriodParameters: ITransactionPostingPeriodParameters;
    serviceRestrictionParameters: IServiceRestrictionParameters;
    preliminaryInvoiceParameters: IPreliminaryInvoiceParameters;
    customerIdentificationParameters: ICustomerIdentificationParameters;
    criticalTaskOffice365SynchronizationParameters: ICriticalTaskOffice365SynchronizationParameters;
    matterSecurityParameters: IMatterSecurityParameters;
    invoicingPeriodParameters: IInvoicingPeriodParameter;
    restrictedUserParameters?: IRestrictedUserParameters | null;
  };
}
export interface IMatterSecurityParameters {
  allowMatterAssistantToModifyMatterUserRights?: boolean | null;
  extendedRightsForMatterAndPreliminaryInvoiceUserGroupId?: string | null;
}
export interface IRestrictedUserParameters {
  allowCreateCustomers?: boolean | null;
  allowEditCustomers?: boolean | null;
  allowCreateMatters?: boolean | null;
  allowEditMatters?: boolean | null;
  allowEditDocuments?: boolean | null;
}

export enum InvoicingPeriod {
  NOT_USED = 'not-used',
  WEEKLY = 'weekly',
  TWICE_A_MONTH = 'twice-a-month',
  ONCE_A_MONTH = 'once-a-month',
  EVERY_SECOND_MONTH = 'every-second-month',
  EVERY_THIRD_MONTH = 'every-third-month',
}

export interface IInvoicingPeriodParameter {
  invoicingPeriod: InvoicingPeriod | null | undefined;
}
export interface IDefaultParameters {
  workTimeTypeId: string;
}

export interface ICustomerIdentificationParameters {
  identificationInUse: boolean;
  useLimitedIdentification: boolean;
  identificationUserSecurityGroup: string;
  newTransactionAlertType: AlertType;
  preliminaryInvoiceAlertType: AlertType;
  invoiceAlertType: AlertType;
  advanceInvoiceAlertType: AlertType;
  activationDate: string;
}

export interface IPreliminaryInvoiceParameters {
  accepter: PreliminaryInvoiceAssignTo;
  missingVatAlert: AlertType;
  canDeleteTransactionsFromPreliminaryInvoice: boolean;
  allowDuplicateReviewers: boolean;
  keepPreliminaryInvoiceOnReject: boolean;
  reasonRequiredOnPreliminaryInvoiceReject: boolean;
  acceptingUserGroupId: string;
}

export enum AlertType {
  NOALERT = 'no-alert',
  WARNING = 'warning',
  ERROR = 'error',
}

export enum TransactionAlertSource {
  MATTER = 'matter',
  MATTERPRINCIPAL = 'matter-principal',
  MATTERPAYER = 'matter-payer',
}

export interface IServiceRestrictionParameters {
  newMatterAlertType: AlertType;
  newTransactionAlertType: AlertType;
  transactionAlertSource: TransactionAlertSource;
  minDaysInvoicesOverdueAlert: number;
}

export interface ITransactionPostingPeriodParameters {
  postingPeriodInUse: boolean;
  maximumOpenedPostingPeriodsInWeeks: number;
  allowOpeningOfPostingPeriodsInFuture: boolean;
}

export interface IInvoiceParameters {
  discountPercent?: number;
  currency?: string;
  roundInvoicesToWholeNumber?: boolean;
  useInvoiceDateAsEntryDate?: boolean;
  swedenReferencePermanentLength?: number;
  useCreditorReferenceNumber?: boolean;
  invoicingMemoInUse?: boolean;
  generalExpenseInUse?: boolean;
  generalExpensePercent?: number;
  generalExpenseApplyPrincipalDiscount?: boolean;
  allowDuplicateReviewers?: boolean;
  doNotRemovePreliminaryInvoiceOnReject?: boolean;
  reasonRequiredOnPreliminaryInvoiceReject?: boolean;
  allowDeletionOfTransactionsAndExpensesFromPreliminaryInvoice?: boolean;
  newPreliminaryInvoiceAssignTo?: PreliminaryInvoiceAssignTo;
  companyId?: string;
  preliminaryInvoiceId?: string;
  businessUnitId?: string;
  privatePersonId?: string;
  matterId?: string;
  paymentTermId?: string;
  noticePeriodId?: string;
  generalExpenseTypeId?: string;
  matterPartyId?: string;
  company?: IPrincipalCompany;
  preliminaryInvoice?: IPreliminaryInvoice;
  businessUnit?: IBusinessUnit;
  privatePerson?: IPrincipalPrivatePerson;
  matter?: IMatter;
  paymentTerm?: IPaymentTerm;
  noticePeriod?: INoticePeriod;
  generalExpenseType?: IExpenseType;
  matterParty?: IMatterParties;
}

export interface IFeaturesInUseParameters {
  matterRequestInUse?: boolean;
  opportunityInUse?: boolean;
  prospectInUse?: boolean;
  subMatterInUse?: boolean;
  subscriptionInUse?: boolean;
  customerIdentificationInUse?: boolean;
  internalMessageInUse?: boolean;
  blackListInUse?: boolean;
}

export interface ICriticalTaskOffice365SynchronizationParameters {
  synchronizeCriticalTasksToOffice365: boolean;
}

export interface INoticePeriod extends ITranslatableEntity {
  days: number;
}

export interface IPaymentTerm extends ITranslatableEntity {
  days: number;
}

export enum PreliminaryInvoiceAssignTo {
  OWNER = 'owner',
  RESPONSIBLE_PERSON = 'responsible-person',
}

export interface ITransactionParameters {
  skillRequired: boolean;
  transactionTypeInUse: boolean;
  transactionTypeRequired: boolean;
  invoiceableDurationStep: InvoiceableDurationStep;
  minimumInvoiceableDurationInHours: number;
  currency: string;
  defaultTransactionTypeId?: string;
}

export enum InvoiceableDurationStep {
  NONE = 'none',
  SIX_MINUTES = 'sixMinutes',
  TWELVE_MINUTES = 'twelveMinutes',
  FIFTEEN_MINUTES = 'fifteenMinutes',
  THIRTY_MINUTES = 'thirtyMinutes',
}

export enum InvoiceableDurationValues {
  NONE = 0,
  SIX_MINUTES = 6,
  TWELVE_MINUTES = 12,
  FIFTEEN_MINUTES = 15,
  THIRTY_MINUTES = 30,
}

export enum ParameterEntityType {
  INVOICE = 'invoice',
  INVOICE_PRINTING = 'invoice-printing',
  FEATURES_IN_USE = 'features-in-use',
  SERVICE_RESTRICTION = 'service-restriction',
  TRANSACTION_POSTING_PERIOD = 'transaction-posting-period',
  TRANSACTION = 'transaction',
  PRELIMINARY_INVOICE = 'preliminary-invoice',
  DEFAULT = 'default',
  CUSTOMER_IDENTIFICATION = 'customer-identification',
  PRICING = 'pricing',
  INVOICING_PERIOD = 'invoicing-period',
  MATTER_SECURITY = 'matter-security',
  ENTRIES_SECURITY = 'entries-security',
  CRITICAL_TASK_OFFICE_365_SYNCHRONIZATION = 'critical-task-office-365-synchronization',
  RESTRICTED_USER = 'restricted-user',
}
export interface IGetParameters {
  parametersToGet?: ParameterEntityType[];
}
